import { Plugins } from "./plugins";
import { tokenTypes } from "./token_types";
import { Facets, setPlatformFacets, setCommonFacets } from "./facets";
import { RateLimiter, RateLimiterSettings } from "./rate_limiter/rate_limiter";
import { PredefinedFacetsBuilder } from "./predefined_facets/predefined_facets";
import { version } from "../../lib/version";

const GATEWAY_STG = "https://mcd-gateway.stg-myteksi.com";
const GATEWAY_PROD = "https://mcd-gateway.grabtaxi.com";
const TRANSMITTER_STG = "https://public.stg-myteksi.com";
const TRANSMITTER_PROD = "https://public.grab.com";
const GATEWAY_API_PATH = "/v2/web/track";
const TRANSMITTER_API_PATH = "/x-transmitter/v2";

export function Scribe() {
  const webSDK = "WebSDK";

  const clientOptions = {
    appName: "",
    scribeSource: "",
    xTokenType: "",
    xToken: "",
    facets: new Facets(),
    globalFacets: {},
    gatewayURL: GATEWAY_STG,
    transmitterURL: TRANSMITTER_STG,
    initialized: false,
    keepAlive: false,
  };
  Object.seal(clientOptions);

  let rateLimiter;
  const setOfTokenTypes = new Set(Object.values(tokenTypes));

  const initialize = function (
    applicationName,
    isProduction = false,
    keepAlive = false,
    tenantClient = "",
    overrideValue = "",
    overrideGatewayURL = "",
    overrideTransmitterURL = ""
  ) {
    clientOptions.appName = applicationName;
    if (overrideValue) {
      clientOptions.scribeSource = overrideValue;
    } else {
      clientOptions.scribeSource = [
        webSDK,
        version,
        clientOptions.appName,
        tenantClient,
      ]
        .filter((item) => item.length != 0)
        .join("/");
    }

    clientOptions.gatewayURL =
      (overrideGatewayURL
        ? overrideGatewayURL
        : isProduction
        ? GATEWAY_PROD
        : GATEWAY_STG) + GATEWAY_API_PATH;
    clientOptions.transmitterURL =
      (overrideTransmitterURL
        ? overrideTransmitterURL
        : isProduction
        ? TRANSMITTER_PROD
        : TRANSMITTER_STG) + TRANSMITTER_API_PATH;

    clientOptions.keepAlive = keepAlive;

    if (clientOptions.initialized == false)
      rateLimiter = RateLimiter(RateLimiterSettings, clientOptions);
    clientOptions.initialized = true;

    setPlatformFacets(clientOptions);
    addEventListeners();
  };

  const getSupportedTokenTypes = function () {
    return tokenTypes;
  };

  const setAuthToken = function (token, tokenType = tokenTypes.JWT) {
    if (!checkValidTokenType(tokenType)) {
      const tokenTypeError = "Invalid token type: " + tokenType;
      throw Error(tokenTypeError);
    }

    clientOptions.xTokenType = tokenType;
    clientOptions.xToken = token;
  };

  const sendEvent = function (eventName, facets = {}) {
    if (!checkInitialization())
      throw Error(
        "Scribe client not initialized: Send event can only be called after client is initialized."
      );
    setCommonFacets(clientOptions);
    let eventObj = {};
    eventObj.e = [clientOptions.appName, "track", "web", eventName].join(".");
    eventObj.t = Date.now();
    eventObj.d = Object.assign(
      {},
      clientOptions.facets.build(),
      clientOptions.globalFacets,
      facets
    );
    return rateLimiter.sendEvent(eventObj);
  };

  const getVariable = async function (variable, facets = {}) {
    if (!checkInitialization())
      throw Error(
        "Scribe client not initialized: getVariable can only be called after client is initialized."
      );

    if (variable === undefined) throw Error("Missing param: variable");

    setCommonFacets(clientOptions);

    const varRequestObj = {};
    const allFacets = Object.assign({}, clientOptions.facets.build(), facets);
    varRequestObj.reqBody = Object.assign({}, allFacets, {
      var: variable,
    });

    // for now, we are using this global scope for all services that will use scribe sdk web experimentation
    // in future, if we need application specific scope, we can append the name. i.e "scribesdk-web-" + clientOptions.appName
    varRequestObj.service = "scribesdk-web";
    return rateLimiter.getVariable(varRequestObj);
  };

  const setFacets = function (facets = {}) {
    clientOptions.globalFacets = Object.assign(
      {},
      clientOptions.globalFacets,
      facets
    );
  };

  const getPredefinedFacetsBuilder = () => {
    return PredefinedFacetsBuilder;
  };

  const usePlugin = function () {
    if (clientOptions.plugins === undefined) {
      clientOptions.plugins = Plugins(this);
    }
    clientOptions.plugins.usePlugin(arguments);
  };

  const checkValidTokenType = function (tokenType) {
    return setOfTokenTypes.has(tokenType);
  };

  const checkInitialization = function () {
    return clientOptions.initialized;
  };

  const addEventListeners = function () {
    document.addEventListener("visibilitychange", () => {
      forceUpdate();
    });
    window.addEventListener("beforeunload", () => {
      forceUpdate();
    });
    window.addEventListener("pagehide", () => {
      forceUpdate();
    });
  };

  const forceUpdate = function () {
    return rateLimiter.sendEvent(null, true);
  };

  return Object.freeze({
    initialize,
    getSupportedTokenTypes,
    setAuthToken,
    sendEvent,
    forceUpdate,
    setFacets,
    usePlugin,
    getVariable,
    getPredefinedFacetsBuilder,
  });
}
